import {PayrollPayFrequency} from "../../../__generated__/generated_types";
import {ensureDateInUtc} from "../../../utils/helper-funcs";
import {getLastDayOfMonth} from "../../../utils/months-helper";
import {addDays, generatePayDates, getPayPeriodsPerMonth} from "./payroll-dates";

export type YearSegment = "reforecast" | "budget";

export type MonthReference = {
    year: number;
    monthIndex: number;
    segment: YearSegment;
};


export function sortAndDedupeDates(dates: Date[]): Date[] {
    const byKey = new Map<string, Date>();
    dates.forEach(date => byKey.set(toDateKey(date), ensureDateInUtc(date)!));
    return Array.from(byKey.values()).sort((a, b) => a.getTime() - b.getTime());
}

export function toDateKey(date: Date): string {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

export function buildDatesForMonthByCount(year: number, monthIndex: number, count: number): Date[] {
    const lastDay = getLastDayOfMonth(monthIndex, year);
    const boundedCount = Math.max(0, Math.min(lastDay, Math.floor(count)));
    if(boundedCount == 0) {
        return [];
    }
    if(boundedCount == 1) {
        return [new Date(Date.UTC(year, monthIndex, lastDay))];
    }
    if(boundedCount == 2) {
        return [new Date(Date.UTC(year, monthIndex, 15)), new Date(Date.UTC(year, monthIndex, lastDay))];
    }

    const dates: Date[] = [];
    for(let index = 0; index < boundedCount - 1; index++) {
        const day = Math.max(1, Math.min(lastDay - 1, Math.round(((index + 1) * lastDay) / boundedCount)));
        dates.push(new Date(Date.UTC(year, monthIndex, day)));
    }
    dates.push(new Date(Date.UTC(year, monthIndex, lastDay)));
    return sortAndDedupeDates(dates);
}

export function wrapDateIntoRange(date: Date, rangeStart: Date, rangeEnd: Date): Date {
    const dayMs = 24 * 60 * 60 * 1000;
    const normalized = ensureDateInUtc(date)!;
    const start = ensureDateInUtc(rangeStart)!;
    const end = ensureDateInUtc(rangeEnd)!;
    const totalDays = Math.floor((end.getTime() - start.getTime()) / dayMs) + 1;
    const cycleMs = totalDays * dayMs;
    let timestamp = normalized.getTime();

    while(timestamp < start.getTime()) {
        timestamp += cycleMs;
    }
    while(timestamp > end.getTime()) {
        timestamp -= cycleMs;
    }

    return new Date(timestamp);
}

export function getMonthReferences(budgetYear: number): MonthReference[] {
    const references: MonthReference[] = [];
    for(let monthIndex = 0; monthIndex < 12; monthIndex++) {
        references.push({
            year: budgetYear - 1,
            monthIndex,
            segment: "reforecast"
        });
    }
    for(let monthIndex = 0; monthIndex < 12; monthIndex++) {
        references.push({
            year: budgetYear,
            monthIndex,
            segment: "budget"
        });
    }
    return references;
}

export function getDatesByMonth(customPayDates: Date[], budgetYear: number): Date[][] {
    const monthRefs = getMonthReferences(budgetYear);
    const ret = monthRefs.map(() => [] as Date[]);
    customPayDates.forEach(date => {
        const year = date.getUTCFullYear();
        if(year != budgetYear - 1 && year != budgetYear) {
            return;
        }
        const offset = year == budgetYear - 1 ? 0 : 12;
        const monthDates = ret[offset + date.getUTCMonth()];
        if(monthDates) {
            monthDates.push(ensureDateInUtc(date)!);
        }
    });
    return ret.map(monthDates => monthDates.sort((a, b) => a.getUTCDate() - b.getUTCDate()));
}

export function buildCustomPresetDates(preset: PayrollPayFrequency, budgetYear: number): Date[] {
    const reforecastYear = budgetYear - 1;
    if(preset == PayrollPayFrequency.Weekly || preset == PayrollPayFrequency.BiWeekly) {
        let seedDate = ensureDateInUtc(new Date(`${budgetYear - 2}-12-31`)) ?? new Date(Date.UTC(budgetYear - 2, 11, 31));
        let generated = generatePayDates(preset, reforecastYear, seedDate);
        let periods = getPayPeriodsPerMonth(reforecastYear, generated[0]);
        let guard = 0;
        while(periods.reforecast.sum() != periods.budget.sum() && guard < 14) {
            seedDate = addDays(seedDate, 1);
            generated = generatePayDates(preset, reforecastYear, seedDate);
            periods = getPayPeriodsPerMonth(reforecastYear, generated[0]);
            guard++;
        }
        return generated[0].map(dt => ensureDateInUtc(dt)!);
    }
    return generatePayDates(preset, reforecastYear)[0].map(dt => ensureDateInUtc(dt)!);
}
